/**
 * Client HTTP unique du front.
 *
 * Deux choix qui evitent des ennuis plus tard :
 *  - les URL sont RELATIVES (/api/...) : Vite proxifie en dev, Caddy en prod,
 *    donc le front reste same-origin et le cookie de panier fonctionne sans
 *    configuration SameSite exotique.
 *  - credentials: "include" partout : le panier vit dans un cookie httpOnly.
 */

export interface ApiFieldError {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    override readonly message: string,
    readonly code?: string,
    readonly fields?: ApiFieldError[],
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Le creneau ou le stock vient de partir : le front doit rafraichir. */
  get isConflict() {
    return this.status === 409;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    const err = payload?.error;
    throw new ApiError(
      res.status,
      err?.message ?? "Une erreur est survenue.",
      err?.code,
      err?.fields,
      err?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

/** Construit une query string en ignorant les valeurs vides. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (entries.length === 0) return "";
  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}`;
}

// --- Formes de reponse renvoyees par l API ---------------------------------

export interface CatalogImage {
  url: string;
  alt: string;
}

export interface EquipmentModelSummary {
  id: string;
  slug: string;
  name: string;
  brand: string | null;
  shortDescription: string | null;
  specs: Record<string, string> | null;
  images: CatalogImage[];
  dailyPriceCents: number;
  depositCents: number;
  totalUnits: number;
  category: { slug: string; name: string } | null;
}

export interface ProductSummary {
  id: string;
  slug: string;
  name: string;
  shortDescription: string | null;
  images: CatalogImage[];
  priceCents: number;
  stock: number;
  category: { slug: string; name: string } | null;
}

export interface ServiceSummary {
  id: string;
  slug: string;
  name: string;
  shortDescription: string | null;
  images: CatalogImage[];
  basePriceCents: number;
  flatRate: boolean;
  minDays: number;
  category: { slug: string; name: string } | null;
}

export interface ShowcaseSummary {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  tag: string | null;
  images: CatalogImage[];
  eventDate: string | null;
}

export interface AvailabilityRow {
  modelId: string;
  slug: string;
  name: string;
  dailyPriceCents: number;
  depositCents: number;
  totalUnits: number;
  availableUnits: number;
}

export interface CartLine {
  id: string;
  lineType: "RENTAL" | "PRODUCT" | "SERVICE";
  label: string;
  unitPriceCents: number;
  quantity: number;
  startAt: string | null;
  endAt: string | null;
  netCents: number;
  depositCents: number;
  notes: string | null;
  equipmentModel: { slug: string; images: CatalogImage[]; depositCents: number } | null;
  product: { slug: string; images: CatalogImage[] } | null;
  service: { slug: string; images: CatalogImage[]; flatRate: boolean } | null;
}

export interface Cart {
  id: string;
  status: string;
  vatRegime: string;
  netCents: number;
  vatCents: number;
  grossCents: number;
  depositCents: number;
  dueNowCents: number;
  lines: CartLine[];
}
